// services/appointmentSyncService.js
import { API_URL } from '../config/api';
import { getAuthHeaders } from '../utils/auth';

// const getAuthHeaders = () => {
//   try {
//     const session = localStorage.getItem('userSession');
//     if (session) {
//       const token = JSON.parse(session)?.user?.token;
//       if (token) return { Authorization: `Bearer ${token}` };
//     }
//     const user = localStorage.getItem('user');
//     if (user) {
//       const token = JSON.parse(user)?.token;
//       if (token) return { Authorization: `Bearer ${token}` };
//     }
//   } catch {}
//   return {};
// };

/**
 * Service for syncing appointments and orders bidirectionally
 */
class AppointmentSyncService {
  /**
   * Sync appointment date change to linked order
   * Updates: order DueDate, customer pickup TaskDueDate, QB invoice due date
   * @param {number} appointmentId - Appointment ID
   * @param {string} newEventStart - New start date/time (ISO format)
   * @param {string} newEventEnd - New end date/time (ISO format)
   * @returns {Promise<Object>} Sync result
   */
  async syncAppointmentToOrder(appointmentId, newEventStart, newEventEnd) {
    try {
      console.log(`🔄 Syncing appointment ${appointmentId} to order...`, { newEventStart, newEventEnd });

      const response = await fetch(`${API_URL}/api/appointments/${appointmentId}/sync`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          EventStart: newEventStart,
          EventEnd: newEventEnd
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ Appointment synced to order:', result);

      return {
        success: true,
        ...result.data,
        message: result.message
      };
    } catch (error) {
      console.error('❌ Error syncing appointment to order:', error);
      return {
        success: false,
        error: error.message,
        message: 'Failed to sync appointment changes to order'
      };
    }
  }

  /**
   * Sync order due date change to linked appointment
   * Updates: appointment EventStart and EventEnd
   * @param {number|string} orderNo - Order number
   * @param {string} newDueDate - New due date (ISO format)
   * @returns {Promise<Object>} Sync result
   */
  async syncOrderToAppointment(orderNo, newDueDate) {
    try {
      console.log(`🔄 Syncing order ${orderNo} to appointment...`, { newDueDate });

      const response = await fetch(`${API_URL}/api/orders/${orderNo}/sync-due-date`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          dueDate: newDueDate
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ Order synced to appointment:', result);

      return {
        success: true,
        ...result,
        message: 'Order due date updated and synced to appointment'
      };
    } catch (error) {
      console.error('❌ Error syncing order to appointment:', error);
      return {
        success: false,
        error: error.message,
        message: 'Failed to sync order changes to appointment'
      };
    }
  }

  /**
   * Get linked order information for an appointment
   * @param {number} appointmentId - Appointment ID
   * @returns {Promise<Object>} Appointment and order link info
   */
  async getAppointmentOrderLink(appointmentId) {
    try {
      console.log(`🔍 Fetching order link for appointment ${appointmentId}...`);

      const response = await fetch(`${API_URL}/api/appointments/${appointmentId}/order`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ Appointment order link:', result);

      return {
        success: true,
        appointment: result.appointment,
        order: result.order,
        isLinked: result.isLinked
      };
    } catch (error) {
      console.error('❌ Error fetching appointment order link:', error);
      return {
        success: false,
        error: error.message,
        isLinked: false,
        appointment: null,
        order: null
      };
    }
  }

  /**
   * Check if appointment is linked to an order
   * @param {number} appointmentId - Appointment ID
   * @returns {Promise<boolean>} True if linked
   */
  async isAppointmentLinked(appointmentId) {
    const result = await this.getAppointmentOrderLink(appointmentId);
    return result.isLinked;
  }

  /**
   * Helper to format date for API calls
   * @param {Date|string} date - Date to format
   * @returns {string} ISO formatted date string
   */
  formatDateForAPI(date) {
    if (typeof date === 'string') {
      return new Date(date).toISOString();
    }
    return date.toISOString();
  }
}

// Export singleton instance
export const appointmentSyncService = new AppointmentSyncService();
export default appointmentSyncService;
